// Content + step-builder for the onboarding tutorial (App.js's grand tour on
// first login, and the per-workspace "🎓 Tutorial" button in every sidebar).
//
// Deliberately kept as a plain data file (not JSX) so the copy can be edited
// without touching the tour engine (components/tutorial/*). Each entry below
// is keyed by the same domainKey/functionKey pairs permissionConfig.js's
// DOMAINS already uses — path/label are read from there, not duplicated here.
import { DOMAINS, hasDomainAccess, hasFunctionAccess } from './permissionConfig';

// Same order as Landing.jsx's DEPARTMENTS (the tile order the user actually
// sees first) — 'warehouse' has no Landing tile at all (warehouse-role users
// are redirected straight to /warehouse in App.js), so it's excluded here.
const LANDING_DOMAIN_ORDER = [
    'invoicing', 'po_so_lifecycle', 'inventory', 'sales', 'dms', 'platform', 'd2c', 'pod_upload',
];

const DOMAIN_INTRO = {
    sales: "Sales is your day-to-day view of what's actually selling — daily MIS, SKU-level performance, city analytics and platform-wise reports.",
    inventory: 'Inventory tracks stock as it moves — record what comes in and goes out, and see current stock across every SKU in one dashboard.',
    dms: "DMS is the distributor side of the business — each distributor's own sales and inventory, so you know what to replenish and when.",
    platform: 'Platform tells you where your SKUs are actually listed and buyable right now, city by city and pincode by pincode, across quick-commerce apps.',
    po_so_lifecycle: 'PO & SO Lifecycle follows one purchase order end to end — from the order landing, through dispatch, invoicing and delivery, to the goods-received note closing it out.',
    invoicing: 'Invoicing is where dispatch data becomes an actual invoice — advance booking, the invoice queue, and every invoice you\'ve raised.',
    d2c: 'D2C tracks your own Shopify store\'s order fulfillment — fill rate, trends, and orders still waiting to ship.',
    pod_upload: "POD Upload is a quick mobile page for the field team to record a hand delivery's proof — no laptop needed.",
};

// content[domainKey][functionKey] = { description, tip, target }
// `target` is a data-tutorial="…" CSS attribute selector added to the real
// page (see the corresponding page file) — the sparkle points at it.
const CONTENT = {
    sales: {
        dashboard: {
            description: 'Your daily sales dashboard — units, revenue and trends across every platform, with city and SKU breakdowns.',
            tip: "Try switching the date range to 'Last 30 days' to see a recent trend instead of just today.",
            target: '[data-tutorial="sales-date-range"]',
        },
        festive_season_trends: {
            description: "A retrospective of last year's festive season (Sep–Dec) — units by SKU, YoY growth and platform split — built for planning this year's.",
            tip: 'Filter by platform or month to see which SKUs actually drove the festive spike.',
            target: '[data-tutorial="festive-filters"]',
        },
    },
    inventory: {
        stock_dashboard: {
            description: 'Live current-stock levels for every SKU, computed from every inward and outward entry recorded.',
            tip: 'Search for a SKU by name or code to jump straight to its current quantity.',
            target: '[data-tutorial="stock-search"]',
        },
        inward: {
            description: 'Record stock coming in — a new batch received into the warehouse.',
            tip: 'Fill in the SKU, quantity and date, then save — it updates the Stock Dashboard immediately.',
            target: '[data-tutorial="inward-form"]',
        },
        outward: {
            description: 'Record stock going out — dispatched, transferred, or used up.',
            tip: 'Same flow as Inward, just on the other side of the ledger.',
            target: '[data-tutorial="outward-form"]',
        },
        sku_master: {
            description: 'The master list of every SKU the business sells — names, codes and details in one place.',
            tip: 'Use this to look up the exact SKU code before entering an Inward/Outward record.',
            target: '[data-tutorial="sku-master-table"]',
        },
        card_inventory: {
            description: 'Tracks physical greeting-card stock alongside product SKUs.',
            tip: 'Filter by card design to see what\'s left in stock.',
            target: '[data-tutorial="card-inventory-table"]',
        },
    },
    dms: {
        uprising_inventory: {
            description: "Uprising distributor's own sales and inventory feed, so you can see their stock position without asking them directly.",
            tip: 'Check this before approving a new PO from Uprising — confirms they actually need it.',
            target: '[data-tutorial="dms-uprising-table"]',
        },
        fatehpuria_inventory: {
            description: "Fatehpuria distributor's sales and inventory feed — same idea as Uprising, different distributor.",
            tip: 'Same workflow as Uprising Inventory.',
            target: '[data-tutorial="dms-fatehpuria-table"]',
        },
        distributor_po: {
            description: 'Purchase orders raised by distributors — review and process them from here before they become Sales Orders.',
            tip: "Open a PO to see its full line-item breakdown before approving it.",
            target: '[data-tutorial="distributor-po-table"]',
        },
        distributor_master: {
            description: "The master list of distributors and the SKUs each one carries.",
            tip: "Use this to confirm a distributor's mapped SKUs before raising a PO on their behalf.",
            target: '[data-tutorial="distributor-master-table"]',
        },
    },
    platform: {
        sku_availability: {
            description: 'Shows exactly where each SKU is listed and in-stock right now on quick-commerce apps, by city and pincode.',
            tip: 'Filter by city to spot where a SKU has quietly gone out of stock.',
            target: '[data-tutorial="platform-filters"]',
        },
    },
    po_so_lifecycle: {
        supply_chain_dashboard: {
            description: 'A management rollup of every PO in flight — cycle times (PO→Invoice, PO→Dispatched, PO→Delivered), a delivery funnel, expiry/in-transit watchlists, and box/weight discrepancy vs Shiprocket.',
            tip: "Try 'Last 30 days' in Quick Range, then open the Box & Weight Discrepancy card to see packed-vs-courier variance.",
            target: '[data-tutorial="scm-quick-range"]',
        },
        po_tracker: {
            description: 'Every PO with its full lifecycle in one row — SO, invoice, GRN, AWB/tracking status and POD, all in one table.',
            tip: "Filter Tracking Status to 'Pending' to see every PO still waiting on a tracking update, then try Download POD to bulk-export proofs of delivery.",
            target: '[data-tutorial="po-tracker-download-pod"]',
        },
        b2b_dispatch: {
            description: 'Packing-slip export for dispatch, plus "Update PO Record" for manually recording an AWB/status on shipments that never touch Shiprocket (e.g. hand deliveries).',
            tip: "Use 'Update PO Record' to search a PO and set its AWB/status by hand.",
            target: '[data-tutorial="b2b-dispatch-tabs"]',
        },
        so_kam_review: {
            description: "Each KAM's Sales Orders queued for review, with current stock alongside every SKU line so a shortfall is obvious before dispatch.",
            tip: 'Check the stock column before approving — it flags SKUs that are short.',
            target: '[data-tutorial="so-kam-table"]',
        },
        grn_management: {
            description: 'Goods Received Notes — confirms what actually arrived at the customer vs what was invoiced, and flags quantity discrepancies.',
            tip: "Filter to 'Open' discrepancies to see what still needs resolving.",
            target: '[data-tutorial="grn-filters"]',
        },
        rtv_record: {
            description: "Return-to-Vendor tracker — every RTV, purchase return, debit note and purchase invoice mail from Blinkit, Zepto, Nykaa, Swiggy and Dorabjee's, captured automatically with its attachments.",
            tip: 'Change the Status dropdown as a pickup progresses; click a document chip to preview it.',
            target: '[data-tutorial="rtv-table"]',
        },
        box_images: {
            description: 'Photos of packed boxes for a dispatch, kept for proof/reference in case of a delivery dispute.',
            tip: 'Search by PO number to pull up that shipment\'s packed-box photos.',
            target: '[data-tutorial="box-images-search"]',
        },
        institutional_orders: {
            description: 'Institutional/bulk orders that follow a different flow from regular platform POs.',
            tip: 'Use this for corporate gifting or bulk institutional orders specifically.',
            target: '[data-tutorial="institutional-orders-table"]',
        },
    },
    invoicing: {
        invoice_queue: {
            description: 'Every invoice waiting to be generated from dispatch data, in one queue.',
            tip: "Process the oldest entries first to keep the queue from backing up. ⚠ If an entry is still sitting here but you've already shared that invoice with the buyer, don't re-generate it — contact the developer first.",
            target: '[data-tutorial="invoice-queue-table"]',
        },
        so_queue: {
            description: 'Sales Orders awaiting invoicing — the step right before an invoice is raised (Cancel SO also lives here, for orders that need to be pulled).',
            tip: "Click any SO row to open its full details — buyer, SKU lines and quantities — before deciding to approve or cancel it.",
            target: '[data-tutorial="so-queue-table"]',
        },
        distributor_performa_invoice: {
            description: 'Proforma invoices for Distributor and Institutional orders — the pre-invoice document sent before the real one.',
            tip: 'Confirm quantities here before the final invoice is generated.',
            target: '[data-tutorial="proforma-table"]',
        },
        invoices: {
            description: 'Every invoice actually raised, searchable by invoice no., buyer or PO number. Click any row to open its full detail — buyer/consignee, tax breakdown and every SKU line item. From that detail view, use "Show Images" to see the packed box photos for that shipment, "Show Invoice PDF" to view or download the actual invoice, and "Packing Slip" to get the dispatch packing slip — all three in one place.',
            tip: "Search by invoice no. or PO number to jump straight to one invoice, then click it and try Show Images / Show Invoice PDF.",
            target: '[data-tutorial="invoices-search"]',
        },
    },
    d2c: {
        shopify_tracker: {
            description: 'Tracks your Shopify orders end to end — fill rate, order trends, and orders still unfulfilled.',
            tip: 'Check the unfulfilled-orders view first — that\'s the one that needs daily attention.',
            target: '[data-tutorial="shopify-filters"]',
        },
    },
    pod_upload: {
        pod_upload_access: {
            description: 'Type or scan an invoice number, take a photo of the delivered goods, and submit — no laptop needed in the field.',
            tip: 'Works straight from a phone browser — no app install required.',
            target: '[data-tutorial="pod-upload-form"]',
        },
    },
};

// Builds the ordered, permission-filtered step list for one tour.
// mode: 'grand' (the full first-login tour across every accessible
// workspace) or a domainKey (a single workspace's own tour, from that
// workspace's "🎓 Tutorial" button).
export function buildTourSteps(user, mode) {
    const steps = [];
    const domainKeys = mode === 'grand' ? LANDING_DOMAIN_ORDER : [mode];

    if (mode === 'grand') {
        domainKeys.forEach((domainKey) => {
            if (!hasDomainAccess(user, domainKey)) return;
            const domain = DOMAINS.find((d) => d.key === domainKey);
            if (!domain) return;
            steps.push({
                type: 'landing-tile',
                domainKey,
                path: '/landing',
                target: `[data-tutorial="landing-tile-${domainKey}"]`,
                title: domain.label,
                description: DOMAIN_INTRO[domainKey],
            });
        });
    }

    domainKeys.forEach((domainKey) => {
        if (!hasDomainAccess(user, domainKey)) return;
        const domain = DOMAINS.find((d) => d.key === domainKey);
        if (!domain) return;

        const firstPath = domain.functions.find((f) => hasFunctionAccess(user, domainKey, f.key))?.path;
        steps.push({
            type: 'domain-intro',
            domainKey,
            path: firstPath,
            target: null,
            title: domain.label,
            description: DOMAIN_INTRO[domainKey],
        });

        const seenPaths = new Set();
        domain.functions.forEach((fn) => {
            if (!hasFunctionAccess(user, domainKey, fn.key)) return;
            if (seenPaths.has(fn.path)) return; // action-only keys (e.g. po_tracker_cancel) share a page with one already stepped through
            const content = CONTENT[domainKey]?.[fn.key];
            if (!content) return; // no authored content for this key (e.g. an action-only permission with nothing to show)
            seenPaths.add(fn.path);
            steps.push({
                type: 'subpage',
                domainKey,
                functionKey: fn.key,
                path: fn.path,
                target: content.target,
                title: fn.label,
                description: content.description,
                tip: content.tip,
            });
        });
    });

    return steps;
}
